/**
 * FunnelAreaChart - Core Component
 *
 * Интерактивный график динамики организаций по этапам воронки.
 * Использует Mantine AreaChart с Points Labels.
 *
 * Props:
 * - period: 'month' | '3months' | '6months' | 'year'
 * - granularity: 'daily' | 'weekly' | 'monthly'
 * - visibleStages: string[] - массив кодов видимых этапов
 * - showTotal: boolean - показывать линию "Всего"
 * - onPeriodChange: (period) => void
 * - onGranularityChange: (granularity) => void
 * - onStageToggle: (stageCode) => void
 * - onTotalToggle: () => void
 * - height: number - высота графика
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Group,
  Stack,
  Text,
  Loader,
  Center,
  Alert,
  SegmentedControl,
  Checkbox,
  Paper,
  SimpleGrid,
} from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { IconAlertCircle } from '@tabler/icons-react';
import tokens from '../../theme/designTokens';
import {
  COOPERATION_STAGES,
  TOTAL_LINE_COLOR,
  PERIOD_OPTIONS,
  GRANULARITY_OPTIONS,
} from '../../constants/cooperationStages';

// API для получения данных
const reportsApi = {
  async getFunnelChartData(period, granularity) {
    const params = new URLSearchParams({ period, granularity });
    const response = await fetch(`/api/reports/funnel-chart?${params}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('У вас нет доступа к отчётам');
      }
      throw new Error('Не удалось загрузить данные графика');
    }

    return response.json();
  },
};

const FunnelAreaChart = ({
  period = 'month',
  granularity = 'daily',
  visibleStages = null,
  showTotal = true,
  onPeriodChange,
  onGranularityChange,
  onStageToggle,
  onTotalToggle,
  onSelectAll,
  height = 400,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Внутреннее состояние для видимости (если не управляется извне)
  const [internalVisibleStages, setInternalVisibleStages] = useState(
    COOPERATION_STAGES.map((s) => s.code)
  );
  const [internalShowTotal, setInternalShowTotal] = useState(true);

  // Используем внешнее или внутреннее состояние
  const activeStages = visibleStages ?? internalVisibleStages;
  const activeTotalVisible = onTotalToggle ? showTotal : internalShowTotal;

  // Загрузка данных
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await reportsApi.getFunnelChartData(period, granularity);

      // Преобразуем данные для AreaChart
      const transformed = data.snapshots.map((snapshot) => {
        const date = new Date(snapshot.date);
        const formattedDate = date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          timeZone: 'Europe/Moscow',
        });

        const point = { date: formattedDate };

        // Добавляем данные по каждому этапу
        COOPERATION_STAGES.forEach((stage) => {
          point[stage.code] = snapshot.stage_counts[stage.code] || 0;
        });

        // Добавляем общее количество
        point.total = snapshot.total;

        return point;
      });

      setChartData(transformed);
    } catch (err) {
      console.error('Failed to fetch funnel chart data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, granularity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Проверка состояния "все выбрано"
  const allStagesSelected = activeStages.length === COOPERATION_STAGES.length;
  const someStagesSelected = activeStages.length > 0 && !allStagesSelected;
  const allSelected = allStagesSelected && activeTotalVisible;

  // Обработчики переключения видимости
  const handleStageToggle = (stageCode) => {
    if (onStageToggle) {
      onStageToggle(stageCode);
    } else {
      setInternalVisibleStages((prev) =>
        prev.includes(stageCode)
          ? prev.filter((s) => s !== stageCode)
          : [...prev, stageCode]
      );
    }
  };

  const handleTotalToggle = () => {
    if (onTotalToggle) {
      onTotalToggle();
    } else {
      setInternalShowTotal((prev) => !prev);
    }
  };

  // Обработчик "Выделить все"
  const handleSelectAll = () => {
    const newSelectAll = !allSelected;
    if (onSelectAll) {
      onSelectAll(newSelectAll);
    } else {
      if (newSelectAll) {
        setInternalVisibleStages(COOPERATION_STAGES.map((s) => s.code));
        setInternalShowTotal(true);
      } else {
        setInternalVisibleStages([]);
        setInternalShowTotal(false);
      }
    }
  };

  // Формируем series для AreaChart
  const series = [
    // Линия "Всего" (если включена)
    ...(activeTotalVisible
      ? [{ name: 'total', color: TOTAL_LINE_COLOR, label: 'Всего' }]
      : []),
    // Линии по этапам (только видимые)
    ...COOPERATION_STAGES.filter((stage) => activeStages.includes(stage.code)).map(
      (stage) => ({
        name: stage.code,
        color: stage.color,
        label: stage.name,
      })
    ),
  ];

  if (loading) {
    return (
      <Center py="xl" style={{ minHeight: height }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Ошибка загрузки"
        color="red"
        variant="light"
      >
        {error}
      </Alert>
    );
  }

  if (chartData.length === 0) {
    return (
      <Center py="xl" style={{ minHeight: height }}>
        <Stack align="center" gap="sm">
          <Text c="dimmed" size="lg">Нет данных за выбранный период</Text>
          <Text c="dimmed" size="sm">
            Снимки создаются автоматически каждые 15 минут.
            Первые данные появятся после создания первого снимка.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Панель управления */}
      <Group justify="space-between" wrap="wrap" gap="sm">
        {/* Период */}
        <Group gap="xs">
          <Text size="sm" fw={500}>
            Период:
          </Text>
          <SegmentedControl
            value={period}
            onChange={onPeriodChange}
            size="xs"
            radius="xl"
            data={PERIOD_OPTIONS}
          />
        </Group>

        {/* Гранулярность */}
        <Group gap="xs">
          <Text size="sm" fw={500}>
            Группировка:
          </Text>
          <SegmentedControl
            value={granularity}
            onChange={onGranularityChange}
            size="xs"
            radius="xl"
            data={GRANULARITY_OPTIONS}
          />
        </Group>
      </Group>

      {/* График */}
      <Paper
        p="md"
        radius={16}
        style={{ border: `1px solid ${tokens.colors.neutral[3]}` }}
      >
        <AreaChart
          h={height}
          data={chartData}
          dataKey="date"
          series={series}
          curveType="monotone"
          withLegend={false}
          withPointLabels
          pointLabelProps={{ fontSize: 10 }}
          gridAxis="xy"
          tickLine="xy"
          strokeWidth={2}
          dotProps={{ r: 4 }}
          activeDotProps={{ r: 6 }}
          fillOpacity={0.1}
          tooltipAnimationDuration={200}
          valueFormatter={(value) => value.toLocaleString('ru-RU')}
        />
      </Paper>

      {/* Легенда с чекбоксами */}
      <Paper
        p="md"
        radius={16}
        style={{
          border: `1px solid ${tokens.colors.neutral[3]}`,
          backgroundColor: tokens.colors.neutral[1],
        }}
      >
        <Group justify="space-between" mb="sm">
          <Text size="sm" fw={500}>
            Фильтр этапов:
          </Text>
          <Checkbox
            label={<Text size="sm" fw={500}>Выделить все</Text>}
            checked={allSelected}
            indeterminate={!allSelected && (someStagesSelected || activeTotalVisible)}
            onChange={handleSelectAll}
            size="xs"
            radius="xl"
          />
        </Group>
        <SimpleGrid cols={{ base: 2, xs: 3, sm: 4, md: 5 }} spacing="xs">
          {/* Чекбокс "Всего" */}
          <Checkbox
            label={
              <Group gap={6}>
                <Box
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: TOTAL_LINE_COLOR,
                    borderRadius: 2,
                  }}
                />
                <Text size="sm">Всего</Text>
              </Group>
            }
            checked={activeTotalVisible}
            onChange={handleTotalToggle}
            size="xs"
            radius="xl"
          />

          {/* Чекбоксы этапов */}
          {COOPERATION_STAGES.map((stage) => (
            <Checkbox
              key={stage.code}
              label={
                <Group gap={6}>
                  <Box
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: stage.color,
                      borderRadius: 2,
                    }}
                  />
                  <Text size="sm">{stage.shortName || stage.name}</Text>
                </Group>
              }
              checked={activeStages.includes(stage.code)}
              onChange={() => handleStageToggle(stage.code)}
              size="xs"
              radius="xl"
            />
          ))}
        </SimpleGrid>
      </Paper>
    </Stack>
  );
};

FunnelAreaChart.propTypes = {
  period: PropTypes.oneOf(['month', '3months', '6months', 'year']),
  granularity: PropTypes.oneOf(['daily', 'weekly', 'monthly']),
  visibleStages: PropTypes.arrayOf(PropTypes.string),
  showTotal: PropTypes.bool,
  onPeriodChange: PropTypes.func,
  onGranularityChange: PropTypes.func,
  onStageToggle: PropTypes.func,
  onTotalToggle: PropTypes.func,
  onSelectAll: PropTypes.func,
  height: PropTypes.number,
};

export default FunnelAreaChart;
